import { readFile } from 'node:fs/promises';
import { basename, dirname, join } from 'pathe';

import type { ProjectProfile } from '@/core/types/project-profile.js';
import type { ResolvedArtifact } from '@/core/types/resolution.js';
import { getRuntimeTemplatesRoot } from '@/core/runtime-paths.js';
import { McpConfigManager } from '@/mcp/config-manager.js';
import { TemplateEngine } from '@/templates/engine.js';

import { buildFrameworkFallbackClause } from './framework-fallback-clause.js';

import type {
  AdapterCapabilities,
  AdapterContext,
  AdapterInterface,
  GeneratedFile,
} from '../adapter.interface.js';

export abstract class BaseAdapter implements AdapterInterface {
  protected readonly engine = new TemplateEngine();
  readonly capabilities: AdapterCapabilities = {
    skills: true,
    agents: true,
    hooks: true,
    mcp: true,
    caching: true,
    memory: true,
  };

  abstract readonly type: AdapterInterface['type'];

  protected abstract configTemplateName(): string;
  protected abstract configOutputPath(): string;
  protected abstract skillsRoot(): string;
  protected abstract agentsRoot(): string;
  protected abstract hooksOutputPath(): string;
  protected abstract mcpOutputPath(): string;
  protected abstract cacheOutputPath(): string;
  protected abstract memoryOutputPath(): string;

  getConfigPath(): string {
    return this.configOutputPath();
  }

  getMcpPath(): string {
    return this.mcpOutputPath();
  }

  async generateConfig(context: AdapterContext): Promise<GeneratedFile[]> {
    const files: GeneratedFile[] = [
      {
        path: this.configOutputPath(),
        content: await this.engine.render(
          join(getRuntimeTemplatesRoot(), 'agent-configs', this.configTemplateName()),
          {
            // Issue #229 — the entry file is now a lean stub: a pointer to the
            // framework bootstrap (`AGENT-BOOTSTRAP.md` in the install) plus the
            // graceful-degradation fallback clause and the `Adapter:` footer. The
            // load order and BOTH contracts (narration + decision-pause) live only
            // in the bootstrap, behind its enablement check — never inlined here.
            adapter: this.type,
            frameworkPath: context.frameworkPath,
            frameworkFallback: buildFrameworkFallbackClause(),
          },
        ),
        autoUpdate: true,
      },
    ];

    const hostIgnore = this.hostConfigGitignore();
    if (hostIgnore) {
      files.push(hostIgnore);
    }
    return files;
  }

  /**
   * Basenames of host-executed config files that carry machine-specific hook
   * commands (the absolute `node "<abs>"` invocation, issue #240). Overridden by
   * the adapters that actually wire executed hooks — Claude (`settings.json`),
   * Codex (`hooks.json`), Gemini (`settings.json`). Default: none.
   */
  protected executedHookConfigFiles(): string[] {
    return [];
  }

  /**
   * A nested `.gitignore` in the host config dir that marks the paqad-generated
   * hook config files git-ignored (issue #240, the per-machine decision). Those
   * files now carry an absolute, machine-specific `node "<abs>"` hook command;
   * committing them would break a teammate or CI on a different home dir / OS.
   * Emitted ONLY for adapters that actually wire executed hooks (Claude, Codex,
   * Gemini) — every other adapter's generateConfig output is unchanged. Consistent
   * with the #184 nested-ignore approach (paqad owns a `.gitignore` inside the dir
   * it manages and never edits the project root `.gitignore`). The entry file, MCP
   * config, cache/memory, and skills/agents content are untouched.
   */
  protected hostConfigGitignore(): GeneratedFile | null {
    const executed = this.executedHookConfigFiles();
    if (executed.length === 0) {
      return null;
    }
    const hostDir = dirname(this.hooksOutputPath());
    if (!hostDir || hostDir === '.') {
      return null;
    }
    // The executed hook file(s) plus the hooks sidecar — every file paqad writes
    // here that carries the machine-specific command.
    const ignored = new Set<string>([basename(this.hooksOutputPath()), ...executed]);

    const body = [
      '# >>> paqad-ai managed (do not edit between markers) >>>',
      '# Per-machine generated hook config — carries an absolute, machine-specific',
      '# hook path. Regenerated by onboarding/update; never committed (issue #240).',
      ...[...ignored].sort(),
      '# <<< paqad-ai managed <<<',
      '',
    ].join('\n');

    return { path: join(hostDir, '.gitignore'), content: body, autoUpdate: true };
  }

  async generateSkills(skills: ResolvedArtifact[]): Promise<GeneratedFile[]> {
    return Promise.all(
      skills.map(async (skill) => ({
        path: join(this.skillsRoot(), getSkillBundleRelativePath(skill)),
        content: await readFile(skill.path, 'utf8'),
        autoUpdate: true,
      })),
    );
  }

  async generateAgents(agents: ResolvedArtifact[]): Promise<GeneratedFile[]> {
    return Promise.all(
      agents.map(async (agent) => ({
        path: join(this.agentsRoot(), basename(agent.path)),
        content: await readFile(agent.path, 'utf8'),
        autoUpdate: true,
      })),
    );
  }

  async installHooks(hooks: ResolvedArtifact[]): Promise<GeneratedFile[]> {
    return [
      {
        path: this.hooksOutputPath(),
        content: JSON.stringify(
          hooks.map((hook) => ({ source: hook.source })),
          null,
          2,
        ),
        autoUpdate: true,
      },
    ];
  }

  async installMcp(
    mcpConfigs: ResolvedArtifact[],
    profile: ProjectProfile,
  ): Promise<GeneratedFile[]> {
    const config = new McpConfigManager().generate(profile, this.type);

    return [
      {
        path: config.path,
        content: JSON.stringify(
          {
            ...JSON.parse(config.content),
            resolved_artifacts: mcpConfigs.map((artifact) => artifact.source),
          },
          null,
          2,
        ),
        autoUpdate: true,
      },
    ];
  }

  async configureCaching(profile: ProjectProfile): Promise<GeneratedFile[]> {
    return [
      {
        path: this.cacheOutputPath(),
        content: JSON.stringify(
          {
            enabled: profile.efficiency.skill_caching,
            differential_refresh: profile.efficiency.differential_refresh,
          },
          null,
          2,
        ),
        autoUpdate: true,
      },
    ];
  }

  async configureMemory(profile: ProjectProfile): Promise<GeneratedFile[]> {
    return [
      {
        path: this.memoryOutputPath(),
        content: JSON.stringify(
          {
            context_hit_rate_target: profile.efficiency.context_hit_rate_target,
            mcp_first: profile.efficiency.mcp_first,
          },
          null,
          2,
        ),
        autoUpdate: true,
      },
    ];
  }
}

function getSkillBundleRelativePath(skill: ResolvedArtifact): string {
  const sourceSegments = skill.source.split(/[\\/]+/);
  const sourceSkillsIndex = sourceSegments.lastIndexOf('skills');

  if (sourceSkillsIndex !== -1 && sourceSkillsIndex < sourceSegments.length - 1) {
    return sourceSegments.slice(sourceSkillsIndex + 1).join('/');
  }

  const segments = skill.path.split(/[\\/]+/);
  const skillsIndex = segments.lastIndexOf('skills');

  if (skillsIndex === -1 || skillsIndex === segments.length - 1) {
    return skill.source.includes('/') ? skill.source : basename(skill.path);
  }

  return segments.slice(skillsIndex + 1).join('/');
}
